import { t } from "ttag";

import type {
  EventPrefix,
  MouseflowFormSubmit,
} from "@/features/analytics/models";
import {
  EVENT_PREFIX,
  MOUSEFLOW_FORM_SUBMIT,
} from "@/features/analytics/models";
import { triggerMouseflowFormSubmit } from "@/features/analytics/triggers";
import type { IntakeFormPayload } from "@/features/billing/api";
import {
  useCreatePaymentMethod,
  useCreateSubscriptionsAndPatient,
} from "@/features/billing/hooks";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { getPricesIds } from "@/features/onboarding/utils";
import {
  useCreateOnboardingPayments,
  useKlarna,
} from "@/features/payments/hooks";
import { MissingClientSecretError } from "@/features/payments/utils";
import {
  PAYMENT_METHOD_TYPE,
  PAYMENT_REDIRECT_METHOD_KEY,
} from "@/shared/domains";
import { getAttributionCookie } from "@/shared/lib/attribution-cookie";
import { COUNTRY } from "@/shared/models";

export const useCheckout = ({
  klarnaReturnPath,
  intakeForm,
  eventPrefix = EVENT_PREFIX.NONE,
  formId,
}: {
  klarnaReturnPath?: string;
  intakeForm?: IntakeFormPayload;
  eventPrefix?: EventPrefix;
  formId?: string;
}) => {
  const metadata = useOnboardingStore((state) => state.urlParams);
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const user = useOnboardingUser();
  const everflowData = getAttributionCookie();
  const selectedPriceId =
    selectedCategories[0]?.selectedProduct?.selectedPrice?.id;

  const everflowParams = {
    efTransactionId: everflowData?._ef_transaction_id ?? "",
    affid: everflowData?.affid ?? "",
    oid: everflowData?.oid ?? "",
    prices: selectedPriceId ? [selectedPriceId] : [],
  };
  const createSubscriptionsAndPatientMutation =
    useCreateSubscriptionsAndPatient({
      user,
      checkoutDiscount,
      selectedCategories,
      eventPrefix,
      ...everflowParams,
    });
  const createPaymentMethodMutation = useCreatePaymentMethod();
  const createPurchasesMutation = useCreateOnboardingPayments();

  const selectedPrices = selectedCategories
    .map((category) => category.selectedProduct?.selectedPrice)
    .filter((price) => price !== undefined);

  const priceDiscountCode = selectedPrices.find(
    (price) => price.discount?.isValid,
  )?.discount?.code;

  const couponCode = checkoutDiscount?.code ?? priceDiscountCode;

  const { pay: payWithKlarna, available: klarnaAvailable } = useKlarna({
    price: selectedPrices[0],
  });

  const reportFormSubmit = (submit: MouseflowFormSubmit) => {
    if (formId) {
      triggerMouseflowFormSubmit({ submit, formId });
    }
  };

  const payByCard = async () => {
    if (!user) {
      throw new Error("No user");
    }

    const subscriptionsToCreate = getPricesIds(selectedCategories);

    const paymentMethodId = await createPaymentMethodMutation.mutateAsync({
      address: {
        country: COUNTRY.US,
        state: user?.state,
        city: user.billing.city,
        postal_code: user.billing.zipCode,
        line1: user.billing.address,
        line2: user.billing.apartmentNumber,
      },
      email: user?.email,
      name: user?.firstName,
      phone: user.phoneNumber,
    });

    return await createSubscriptionsAndPatientMutation.mutateAsync({
      userCode: user.code,
      subscriptions: subscriptionsToCreate,
      couponCode,
      paymentMethodId,
      state: user.state,
      city: user.shipping.city,
      zip: user.shipping.zipCode,
      line1: user.shipping.address,
      line2: user.shipping.apartmentNumber,
      phoneNumber: user.phoneNumber,
      metadata,
      isCallCenter,
      ...(intakeForm && { intakeForm }),
    });
  };

  const cardPay = async () => {
    reportFormSubmit(MOUSEFLOW_FORM_SUBMIT.ATTEMPT);
    try {
      const result = await payByCard();
      reportFormSubmit(MOUSEFLOW_FORM_SUBMIT.SUCCESS);
      return result;
    } catch (error) {
      reportFormSubmit(MOUSEFLOW_FORM_SUBMIT.FAILURE);
      throw error;
    }
  };

  const payByKlarna = async () => {
    if (!user) {
      throw new Error(t`User not found.`);
    }

    const prices = getPricesIds(selectedCategories);

    const createdPurchases = await createPurchasesMutation.mutateAsync({
      prices,
      userCode: user.code,
      state: user.state,
      shipping: user.shipping,
      discount: couponCode,
      metadata,
      paymentMethod: PAYMENT_METHOD_TYPE.KLARNA,
    });

    const purchaseToPay = createdPurchases[0];

    if (!purchaseToPay) {
      throw new Error(
        t`There was a problem creating your subscription. Try again later or contact support.`,
      );
    }

    const { clientSecret } = purchaseToPay;

    if (!clientSecret) {
      throw new MissingClientSecretError();
    }

    const returnUrl = new URL(
      klarnaReturnPath ?? ONBOARDING_ROUTE.CHOOSE_TREATMENT,
      window.location.origin,
    );
    returnUrl.searchParams.set(
      PAYMENT_REDIRECT_METHOD_KEY,
      PAYMENT_METHOD_TYPE.KLARNA,
    );

    return payWithKlarna({
      clientSecret,
      billingDetails: {
        email: user.email,
        phone: user.phoneNumber,
        address: {
          country: COUNTRY.US,
          state: user.state,
          city: user.billing.city,
          line1: user.billing.address,
          line2: user.billing.apartmentNumber,
          postal_code: user.billing.zipCode,
        },
      },
      returnUrl: returnUrl.toString(),
    });
  };

  const klarnaPay = async () => {
    reportFormSubmit(MOUSEFLOW_FORM_SUBMIT.ATTEMPT);
    try {
      return await payByKlarna();
    } catch (error) {
      reportFormSubmit(
        error instanceof MissingClientSecretError
          ? MOUSEFLOW_FORM_SUBMIT.SUCCESS
          : MOUSEFLOW_FORM_SUBMIT.FAILURE,
      );
      throw error;
    }
  };

  return {
    card: {
      pay: cardPay,
    },
    klarna: {
      pay: klarnaPay,
      available: klarnaAvailable,
    },
  };
};
